import { useState } from 'react';
import { create } from '@bufbuild/protobuf';
import { App } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { backgroundTaskApi } from '@/api/backgroundTask.api';
import { ListRequestSchema } from '@/proto/agentplatform/httpv2/common_pb';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { TaskStatus } from '@/proto/agentplatform/v1/harness_pb';

function formatTimestamp(timestamp: number | bigint | undefined) {
  if (!timestamp) return '-';
  const ms = Number(timestamp) / 1000000;
  if (ms <= 0) return '-';
  return new Date(ms).toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

function isCancellable(status: TaskStatus): boolean {
  return (
    status === TaskStatus.PENDING ||
    status === TaskStatus.RUNNING ||
    status === TaskStatus.WAITING_HITL
  );
}

function TaskStatusBadge({ status }: { status: TaskStatus }) {
  switch (status) {
    case TaskStatus.PENDING:
      return (
        <Badge variant="outline" className="text-yellow-600 border-yellow-300">
          等待中
        </Badge>
      );
    case TaskStatus.RUNNING:
      return (
        <Badge variant="outline" className="text-blue-600 border-blue-300">
          运行中
        </Badge>
      );
    case TaskStatus.WAITING_HITL:
      return (
        <Badge variant="outline" className="text-orange-600 border-orange-300">
          等待审批
        </Badge>
      );
    case TaskStatus.COMPLETED:
      return (
        <Badge variant="outline" className="text-green-600 border-green-300">
          已完成
        </Badge>
      );
    case TaskStatus.FAILED:
      return (
        <Badge variant="outline" className="text-red-600 border-red-300">
          失败
        </Badge>
      );
    case TaskStatus.CANCELLED:
      return (
        <Badge variant="outline" className="text-gray-600 border-gray-300">
          已取消
        </Badge>
      );
    default:
      return <Badge variant="outline">未知</Badge>;
  }
}

export function BackgroundTaskTab({ appId }: { appId: string }) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [cancelTarget, setCancelTarget] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const { message } = App.useApp();

  const { data, isLoading } = useQuery({
    queryKey: ['background-tasks', 'list', appId, page, pageSize],
    queryFn: () =>
      backgroundTaskApi.list(
        create(ListRequestSchema, {
          page,
          pageSize,
          filter: { application_id: appId },
        })
      ),
    enabled: !!appId,
  });

  const cancelMutation = useMutation({
    mutationFn: (taskId: string) => backgroundTaskApi.cancel(taskId),
    onSuccess: () => {
      setCancelTarget(null);
      message.success('任务已取消');
      void queryClient.invalidateQueries({ queryKey: ['background-tasks', 'list', appId] });
    },
    onError: (error: unknown) => {
      const msg = error instanceof Error ? error.message : '取消失败';
      message.error(msg);
    },
  });

  const tasks = data?.data ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>后台任务</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-16">
            <div className="flex items-center gap-3 text-muted-foreground">
              <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                />
              </svg>
              <span className="text-sm">加载中...</span>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>后台任务</CardTitle>
        </CardHeader>
        <CardContent>
          {tasks.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <p className="text-lg font-medium mb-2">暂无后台任务</p>
              <p className="text-sm">当前实例尚未创建委托任务</p>
            </div>
          ) : (
            <div>
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="px-3 py-3 text-left font-medium">任务 ID</th>
                      <th className="px-3 py-3 text-left font-medium">子代理</th>
                      <th className="px-3 py-3 text-left font-medium">状态</th>
                      <th className="px-3 py-3 text-left font-medium">创建时间</th>
                      <th className="px-3 py-3 text-left font-medium">完成时间</th>
                      <th className="px-3 py-3 text-left font-medium">操作</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {tasks.map((task) => (
                      <tr key={task.id} className="hover:bg-muted/30 transition-colors">
                        <td className="px-3 py-3">
                          <code className="text-xs bg-muted px-2 py-1 rounded font-mono">
                            {task.id}
                          </code>
                        </td>
                        <td className="px-3 py-3">
                          <code className="text-xs bg-muted px-2 py-1 rounded font-mono">
                            {task.subApplicationId}
                          </code>
                        </td>
                        <td className="px-3 py-3">
                          <TaskStatusBadge status={task.status} />
                        </td>
                        <td className="px-3 py-3 text-muted-foreground">
                          {formatTimestamp(task.createdAt)}
                        </td>
                        <td className="px-3 py-3 text-muted-foreground">
                          {task.finishedAt > 0 ? formatTimestamp(task.finishedAt) : '-'}
                        </td>
                        <td className="px-3 py-3">
                          {isCancellable(task.status) ? (
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-7 text-destructive border-destructive/30 hover:bg-destructive/10"
                              disabled={cancelMutation.isPending && cancelTarget === task.id}
                              onClick={() => setCancelTarget(task.id)}
                            >
                              取消
                            </Button>
                          ) : (
                            <span className="text-xs text-muted-foreground">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4">
                  <span className="text-sm text-muted-foreground">共 {total} 条记录</span>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-1.5">
                      <span className="text-sm text-muted-foreground">每页</span>
                      <select
                        value={pageSize}
                        onChange={(e) => {
                          setPageSize(Number(e.target.value));
                          setPage(1);
                        }}
                        className="h-7 rounded-md border border-input bg-background px-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring cursor-pointer"
                      >
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                        <option value={50}>50</option>
                      </select>
                      <span className="text-sm text-muted-foreground">条</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 w-7 p-0"
                        disabled={page <= 1}
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={2}
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                        </svg>
                      </Button>
                      <div className="flex items-center gap-1 px-2 py-0.5 rounded-md bg-background border text-sm">
                        <span className="font-medium">{page}</span>
                        <span className="text-muted-foreground">/</span>
                        <span className="text-muted-foreground">{totalPages}</span>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 w-7 p-0"
                        disabled={page >= totalPages}
                        onClick={() => setPage((p) => p + 1)}
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={2}
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                        </svg>
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={!!cancelTarget} onOpenChange={(open) => !open && setCancelTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>确认取消任务？</AlertDialogTitle>
            <AlertDialogDescription>
              将停止子代理运行，并把任务标记为已取消。此操作不可撤销。
              {cancelTarget ? (
                <>
                  <br />
                  <code className="text-xs break-all">{cancelTarget}</code>
                </>
              ) : null}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>返回</AlertDialogCancel>
            <AlertDialogAction
              disabled={cancelMutation.isPending || !cancelTarget}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                if (cancelTarget) {
                  cancelMutation.mutate(cancelTarget);
                }
              }}
            >
              {cancelMutation.isPending ? '取消中…' : '确认取消'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
